/** Nonblocking marble introductions mask optional artwork preparation. Native
 * puzzle input is ready throughout; a first gesture or Skip dismisses the scene.
 * Decode failure retains the CSS fallback and never gates progression. */
export function createMarbleScenes(ctx, { host, board, playfield, allowed }) {
  let serial = 0,
    timer = 0,
    disposed = false,
    first = true;
  const animations = new Set();
  const panel = ctx.e('div', 'marble-intro');
  panel.hidden = true;
  const art = ctx.e('div', 'marble-intro-art');
  art.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 3; i++) {
    const ball = ctx.e('i');
    ball.style.setProperty('--piece-colour', `var(--game-colour-${[0, 2, 3][i]})`);
    art.append(ball);
  }
  const emblem = ctx.e('strong');
  emblem.setAttribute('aria-hidden', 'true');
  const skip = ctx.e('button', 'marble-intro-skip');
  skip.type = 'button';
  skip.setAttribute('aria-label', (ctx.uiText ?? ((s) => s))('Skip introduction'));
  if (ctx.icon) skip.append(ctx.icon('chevron-right'));
  else skip.textContent = '›';
  panel.append(art, emblem, skip);
  playfield.append(panel);
  function cancel() {
    serial++;
    clearTimeout(timer);
    for (const animation of animations) animation.cancel();
    animations.clear();
    panel.hidden = true;
  }
  function show(symbol, description) {
    cancel();
    if (disposed || !allowed() || document.hidden || !host.isConnected) return;
    emblem.textContent = symbol;
    panel.setAttribute('aria-label', description);
    panel.setAttribute('role', 'group');
    panel.hidden = false;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches)
      for (const [index, ball] of art.children.entries()) {
        if (typeof ball.animate !== 'function') continue;
        const animation = ball.animate(
          [
            { transform: 'translateY(12px) scale(.6)', opacity: 0 },
            { transform: 'translateY(-6px) scale(1.08)', opacity: 1, offset: 0.5 },
            { transform: 'translateY(0) scale(1)', opacity: 1 },
          ],
          {
            duration: 650,
            delay: index * 90,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            fill: 'backwards',
          },
        );
        animations.add(animation);
        void animation.finished.catch(() => {});
      }
    const token = serial;
    timer = setTimeout(() => {
      if (token === serial) cancel();
    }, 1400);
  }
  function prepare() {
    if (!first) return;
    first = false;
    queueMicrotask(() => {
      if (disposed || !host.isConnected || !allowed()) return;
      show(
        '● → ●',
        'Pocket Marbles. Match the top colours. Play immediately or skip this introduction.',
      );
      // CSS uses this same URL on phone widths, so decode shares the resource.
      // The intro has a fixed deadline independent of network or decode success.
      if (typeof Image === 'undefined') return;
      const image = new Image();
      image.src = new URL('../assets/game-marble-observatory-v1-640.webp', import.meta.url).href;
      void image.decode?.().catch(() => {});
    });
  }
  function chapter(previous, next) {
    const before = previous.data,
      after = next.data;
    if (before.stage === after.stage) return;
    if (after.stage === 3) show(`+●  ${after.colours}`, 'A new colour joins the puzzle.');
    else if (after.stage === 7)
      show('? → ●', 'Mystery marbles. Grey colours reveal when uncovered; one spare tube.');
    else if (after.stage === 11)
      show(`✦ ${after.colours} ✦`, 'Expert puzzles. Eight colours and more concealed marbles.');
  }
  board.addEventListener('pointerdown', cancel);
  board.addEventListener('keydown', cancel);
  skip.addEventListener('click', cancel);
  return {
    prepare,
    chapter,
    cancel,
    dispose() {
      disposed = true;
      cancel();
      board.removeEventListener('pointerdown', cancel);
      board.removeEventListener('keydown', cancel);
      skip.removeEventListener('click', cancel);
      panel.remove();
    },
  };
}
